import { lastValueFrom } from 'rxjs';
import { limit } from 'firebase/firestore';
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { collection, doc, Firestore, getDoc, getDocs, query, where } from '@angular/fire/firestore';
import { CheckHCPPhoneValidatedResponse } from '@aiii/aiii-types/product/med/api/hcp/check-hcp-phone-validated-response';
import { CheckHcpExistRequest } from '@aiii/aiii-types/product/med/api/hcp/check-hcp-exist-request';
import { RegisterHcpRequest } from '@aiii/aiii-types/product/med/api/hcp/register-hcp-request';
import { HcpComprehensiveInfoView } from '@aiii/aiii-types/product/med/big-query-schema/hcp/hcp-comprehensive-info-view';
import { HCP } from '@aiii/aiii-types/product/med/role/hcp';
import { Department } from '@aiii/aiii-types/product/med/big-query-schema/department/department';
import { Job } from '@aiii/aiii-types/product/med/big-query-schema/job/job';
import { MedicalInstitution } from '@aiii/aiii-types/product/med/big-query-schema/medicalInstitution/medicalInstitution';

import { environment } from 'src/environments/environment';
import { FIREBASE_REFERENCES } from '../shared/methods/firebase.module';
import { SharedService } from '../shared/shared.service';
import { GET_SITE } from '../shared/providers/get-site.provider';
import {
  ElililyHcpByLineUserIdResponse,
  ElililyHcpTerritoriesResponse,
  ElililyLineUserResponse,
  HcpVerifyResult,
} from './typings';
import { HcpServiceInterface } from './hcp.service.interface';

@Injectable({
  providedIn: 'root',
})
export class HcpService implements HcpServiceInterface {

  constructor(
    @Inject(FIREBASE_REFERENCES.TAIWAN_FIRESTORE)
    private readonly twFirestore: Firestore,
    private readonly firestore: Firestore,
    private sharedService: SharedService,
    private http: HttpClient,
    @Inject(GET_SITE) private site: string
  ) {}

  async getSiteLiffLogoUrl(site: string): Promise<string> {
    const siteDocRef = doc(this.firestore, 'sites', site);
    const docSnapshot = await getDoc(siteDocRef);

    if (docSnapshot.exists()) {
      return (docSnapshot.get('medOption.liffConfig.logoUrl') as string) ?? '';
    } else {
      return '';
    }
  }

  async getMedPrivacyPolicy(site: string): Promise<any> {
    let medPrivacyPolicy: any | undefined = undefined;
    const medPrivacyPolicyColRef = collection(this.twFirestore, `sites/${site}/medPrivacyPolicies`);
    // query isPublish = true , 只取 limit 1
    const medPrivacyPolicyQuery = query(medPrivacyPolicyColRef, where('isPublish', '==', true), limit(1));
    const querySnapshot = await getDocs(medPrivacyPolicyQuery);

    console.log('querySnapshot', querySnapshot.empty);
    if (!querySnapshot.empty) {
      medPrivacyPolicy = querySnapshot.docs[0].data() as any;
    }

    return medPrivacyPolicy;
  }

  async getMedicalInstitution(site: string, medicalInstitutionKey: string) {
    let medicalInstitution: any | undefined = undefined;
    const medicalInstitutionDef = doc(this.twFirestore, `/sites/${site}/medicalInstitution/${medicalInstitutionKey}`);
    const medicalInstitutionDoc = await getDoc(medicalInstitutionDef);

    if (medicalInstitutionDoc.exists()) {
      medicalInstitution = medicalInstitutionDoc.data() as any;
    }

    return medicalInstitution;
  }

  async getMedicalInstitutionList(site: string): Promise<MedicalInstitution[]> {
    const medicalInstitutionColRef = collection(this.twFirestore, `/sites/${site}/medicalInstitution`);
    const medicalInstitutionSnapshot = await getDocs(medicalInstitutionColRef);
    return medicalInstitutionSnapshot.docs.map((doc) => doc.data() as MedicalInstitution);
  }

  async getHcp(site: string, hcpKey: string) {
    const rsp = await this.sharedService
      .ajaxJsonPost(`${environment.cloudrun.med}/hcp/get-hcp`, {
        site: site,
        hcpKey: hcpKey,
      })
      .catch((err: any) => {
        return undefined;
      });

    return rsp;
  }

  async getHcpByLINEUserId(site: string, LINEUserId: string): Promise<HCP | undefined> {
    const roleHcpRef = collection(this.twFirestore, `/sites/${site}/hcp`);
    const roleHcpQuery = query(roleHcpRef, where('LINEUserId', '==', LINEUserId));
    const roleHcpSnapshot = await getDocs(roleHcpQuery);

    let hcp: HCP | undefined = undefined;
    if (!roleHcpSnapshot.empty) {
      hcp = roleHcpSnapshot.docs[0].data() as HCP;
    }

    return hcp;
  }

  /**
   * 根據 line id 取得 hcp 資料
   * @param lineUserId
   * @returns
   */
  async getElililyHcpByLineUserId(lineUserId: string): Promise<ElililyHcpByLineUserIdResponse> {
    const response = await fetch(
      `${environment.cloudrun.elililyHcpApi}/elilily-api-med/hcp-line/api/v1/hcps/by-line-user/${encodeURIComponent(lineUserId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseText = await response.text();
    const result = responseText ? (JSON.parse(responseText) as ElililyHcpByLineUserIdResponse) : undefined;

    if (result && (response.ok || response.status === 404)) {
      return result;
    }

    if (!response.ok) {
      throw new Error(`Failed to get HCP by LINE user id, status: ${response.status}`);
    }

    throw new Error('Failed to get HCP by LINE user id, response body is empty');
  }

  /**
   * 根據 line id 取得 line user 資料
   * @param lineUserId
   * @returns
   */
  async getElililyLineUserByLineUserId(lineUserId: string): Promise<ElililyLineUserResponse> {
    const response = await fetch(
      `${environment.cloudrun.elililyHcpApi}/elilily-api-med/line-users/${encodeURIComponent(lineUserId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseText = await response.text();
    const result = responseText ? (JSON.parse(responseText) as ElililyLineUserResponse) : undefined;

    if (result && response.ok) {
      return result;
    }

    if (!response.ok) {
      throw new Error(`Failed to get line user by LINE user id, status: ${response.status}`);
    }

    throw new Error('Failed to get line user by LINE user id, response body is empty');
  }

  /**
   * 根據 eph id 取得 hcp 綁定資料
   * @param ephId
   * @returns
   */
  async getElililyHcpTerritoriesByEphId(ephId: string): Promise<ElililyHcpTerritoriesResponse> {
    const response = await fetch(
      `${environment.cloudrun.elililyHcpApi}/elilily-api-med/hcp-line/api/v1/hcps/${encodeURIComponent(ephId)}/territories`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseText = await response.text();
    const result = responseText ? (JSON.parse(responseText) as ElililyHcpTerritoriesResponse) : undefined;

    if (result && (response.ok || response.status === 404)) {
      return result;
    }

    if (!response.ok) {
      throw new Error(`Failed to get HCP territories by eph id, status: ${response.status}`);
    }

    throw new Error('Failed to get HCP territories by eph id, response body is empty');
  }

  async getMedicalInstitutionListByQuery(site: string, name: string, county: string, district: string): Promise<any> {
    const getMedicalInstitutionListByNameRequest = {
      site: site,
      name: name,
      county: county,
      district: district,
    };

    const rep = await this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/medical-institution/get-site-medical-institution-by-query`,
      getMedicalInstitutionListByNameRequest,
      { 'x-api-key': environment.apiKey.med }
    );

    return rep;
  }

  async getSiteDepartment(site: string): Promise<Department[]> {
    const getSiteDepartment = {
      site: site,
    };

    const rep = await this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/medical-institution/get-site-department`,
      getSiteDepartment,
      { 'x-api-key': environment.apiKey.med }
    );

    return rep;
  }

  async checkHcpExist(checkHcpExistRequest: CheckHcpExistRequest): Promise<any> {
    const rsp = await this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/hcp/check-hcp-exist`,
      checkHcpExistRequest
    );

    console.log('checkHcpExist rsp', rsp);

    return rsp;
  }

  async getHcpListByUserIdList(userIdList: string[]): Promise<any> {
    const hcpListByUserIdListRequest = {
      userIdList: userIdList,
    };

    const rep = await this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/hcp/list-by-user-id-list`,
      hcpListByUserIdListRequest
    );

    return rep.body;
  }

  async checkIsHcpExistByPhone(phone: string): Promise<boolean> {
    const roleHcpRef = collection(this.twFirestore, '/sites/nnhcp/roleHcp');
    const roleHcpQuery = query(roleHcpRef, where('phone', '==', phone));
    const roleHcpSnapshot = await getDocs(roleHcpQuery);

    return !roleHcpSnapshot.empty;
  }

  /**
   * 註冊HCP api
   * @param site
   * @param registerHcpRequest
   * @returns
   */
  async registerHcp(site: string, registerHcpRequest: RegisterHcpRequest): Promise<any> {
    const rsp = await this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/hcp/register-hcp`,
      registerHcpRequest
    );

    return rsp;
  }

  async verifyActivityExistHcp(verifyActivityExistHcpRequest: any) {
    const rsp = await this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/hcp/verify-activity-exist-hcp`,
      verifyActivityExistHcpRequest
    );
    return rsp;
  }

  updatePersonalInfoConsentForm(updatePersonalInfoConsentFormRequest: {
    hcpKey: any;
    diabetesRelatedField: boolean;
    weightLossRelatedField: boolean;
    hemophiliaRelatedField: boolean;
    growthHormoneRelatedField: boolean;
  }) {
    const rsp = this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/hcp/update-hcp-personal-info-consent-form`,
      updatePersonalInfoConsentFormRequest
    );

    return rsp;
  }

  /**
   * 取得當前站台院所的縣市區域資訊
   * @param site
   * @returns
   */
  getSiteHospitalCountyAndDistrict(site: string): Promise<{
    countyList: {
      countyName: string;
      districtList: {
        districtName: string;
      }[];
    }[];
  }> {
    const rsp = this.sharedService.ajaxJsonPost(
      `${environment.cloudrun.med}/medical-institution/get-site-hospital-county-and-district`,
      {
        site: site,
      },
      { 'x-api-key': environment.apiKey.med }
    );

    return rsp;
  }

  createHcp(createHcpRequest: any): Promise<any> {
    const rsp = this.sharedService.ajaxJsonPost(`${environment.cloudrun.med}/hcp/create-hcp`, createHcpRequest);

    return rsp;
  }

  async getJobList(site: string): Promise<Job[]> {
    const jobColRef = collection(this.twFirestore, `/sites/${site}/job`);
    const jobQuery = query(jobColRef, where('role', '==', 'hcp'));
    const jobSnapshot = await getDocs(jobQuery);

    let jobList: any[] = [];
    if (!jobSnapshot.empty) {
      jobList = jobSnapshot.docs.map((doc) => doc.data());
    }

    return jobList;
  }

  async getSiteMedOption(site: string) {
    const siteDocRef = doc(this.firestore, 'sites', site);
    const docSnapshot = await getDoc(siteDocRef);

    return docSnapshot.get('medOption');
  }

  /**
   * 取得手機號碼的的驗證狀態：是否已經被 HCP 驗證過
   * 若已經被驗證過,回傳手機號碼被驗證在哪個 Hcp 相關資訊
   * @param phone
   */
  async checkHcpPhoneValidated(site: string, phone: string): Promise<CheckHCPPhoneValidatedResponse> {
    const rsp = this.sharedService.ajaxJsonPost(`${environment.cloudrun.med}/hcp/check-hcp-phone-validated`, {
      site: site,
      phone: phone,
    });
    return rsp;
  }

  /**
   * TODO:好像不需要
   * 取得站台內已驗證 HCP 列表
   * page 頁數
   * limit 每頁筆數
   * filter 輸入框搜尋字串
   * @returns
   */
  async getSiteVerifyHcpList(option?: {
    page?: number;
    limit?: number;
    filter?: string;
  }): Promise<HcpComprehensiveInfoView[]> {
    const result = await lastValueFrom(
      this.http.post<HcpComprehensiveInfoView[]>(`${environment.cloudrun.med}/hcp/list-backend`, {
        ...option,
        site: this.site,
        sort: 'DESC',
        queryOption: { verificationStatus: ['已驗證'] },
      })
    );
    return result;
  }

  /**
   * 禮來客製化功能
   * 新增並驗證HCP
   * @param token
   * @param LINEUserId
   * @returns
   */
  async createAndVerifyHcp(token: string, LINEUserId: string): Promise<HcpVerifyResult> {
    const rsp = (await lastValueFrom(
      this.http.post(
        `${environment.cloudrun.elililyHcpApi}/hcp/create-hcp-by-token`,
        {
          site: this.site,
          LINEUserId: LINEUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    )) as HcpVerifyResult;

    return rsp;
  }
}
